/**
 * Centralized z-index values for consistent layering across the application
 *
 * Z-index ranges (following best practices):
 * 0-999: Base content and local stacking contexts
 * 1000-1999: Fixed positioned elements (headers, footers, sidebars)
 * 2000-2999: Overlays and backdrops
 * 3000-3999: Modals and dialogs
 * 4000-4999: Notifications and toasts
 * 6000-6999: Popovers and tooltips
 * 9000-9999: Critical system overlays (dev tools, etc.)
 */

export const Z_INDEX = {
  // Base elements
  base: 1,
  content: 10,

  // Fixed positioned elements
  stickyElement: 100,
  header: 1000,
  drawer: 1100,
  drawerBackdrop: 1050,

  // Overlays
  overlay: 2000,
  modalBackdrop: 2100,

  // Modals and dialogs
  modal: 3000,
  dialog: 3100,
  dialogOverlay: 3050,
  alertDialog: 3200,
  alertDialogOverlay: 3150,

  // Notifications
  notification: 4000,
  toast: 4100,

  // Popovers and dropdowns
  dropdown: 6000,
  popover: 6100,
  tooltip: 6200,

  // Critical overlays
  devTools: 9000,
  systemCritical: 9999,
} as const;

// Helper function to get z-index value with optional offset
export const getZIndex = (layer: keyof typeof Z_INDEX, offset = 0): number => {
  return Z_INDEX[layer] + offset;
};

// Tailwind-compatible z-index classes mapping
export const Z_INDEX_CLASSES = {
  base: 'z-[1]',
  content: 'z-[10]',
  stickyElement: 'z-[100]',
  header: 'z-[1000]',
  drawer: 'z-[1100]',
  drawerBackdrop: 'z-[1050]',
  overlay: 'z-[2000]',
  modalBackdrop: 'z-[2100]',
  modal: 'z-[3000]',
  dialog: 'z-[3100]',
  dialogOverlay: 'z-[3050]',
  alertDialog: 'z-[3200]',
  alertDialogOverlay: 'z-[3150]',
  dropdown: 'z-[6000]',
  popover: 'z-[6100]',
  tooltip: 'z-[6200]',
  notification: 'z-[4000]',
  toast: 'z-[4100]',
  devTools: 'z-[9000]',
  systemCritical: 'z-[9999]',
} as const;
